// V48 pp.6–12 and V48C examples pp.1–3. encodeFrame calculates wire lengths.
// Ling Meng, 14 September 2026: persistent switches, 1=on and 0=off.
import {WONLEX_ONLY_TYPES,wonlexSettingPayload,wonlexDecodedParameters} from './wonlex-settings.js';
export const CALLING_CONTROL_TYPES = ["DEVREFUSEPHONESWITCH", "HSW", "TELLINCOMINGCALLSW"];
export const HARDWARE_COMMAND_TYPES = ["HRTSTART", "BODYTEMP2", "REMOVESMS", "PEDO", "PROFILE", "FALLDOWN", "SOS", "PHBX", "SOSSMS", "LOWBAT", "SMSONOFF", "APPLOCK", "BODYTEMP", "BTTIMESET", "WALKTIME", "REMIND", "TAKEPILLS", "HRTINTERVAL", "LZ", "RESET", "POWEROFF", "FACTORY", "SPOF", "LSSET", "WIFIFENCE", "MONITOR", "PW", ...CALLING_CONTROL_TYPES];
HARDWARE_COMMAND_TYPES.push(...WONLEX_ONLY_TYPES);
export const MEASUREMENT_COMMAND_TYPES = ["HRTSTART", "BODYTEMP2"];
export const SENSOR_CONTROL_TYPES = [...MEASUREMENT_COMMAND_TYPES, "BODYTEMP", "BTTIMESET", "HRTINTERVAL"];
export const hardwareReplyName = type => type === "HRTINTERVAL" ? "HRTSTART" : type === 'DPHBX' ? 'PHBX' : type;
const object = value => value !== null && typeof value === "object" && !Array.isArray(value);
const exact = (value, keys) => object(value) && Object.keys(value).length === keys.length && keys.every(key => Object.hasOwn(value, key));
const integer = (v, min, max) => Number.isInteger(v) && v >= min && v <= max;
export const validClockTime = value => typeof value === "string" && /^(?:[01]\d|2[0-3]):[0-5]\d$/.test(value);
const boolean = value => typeof value === "boolean";
const fail = () => { throw Object.assign(new Error("invalid_hardware_command"), { status: 400 }); };
function reminderText(value) {
  if (!exact(value, ["time", "enabled", "repeat", "days"]) || !validClockTime(value.time) || !boolean(value.enabled)
    || !["once", "daily", "weekly"].includes(value.repeat) || typeof value.days !== "string" || !/^[01]{7}$/.test(value.days)
    || (value.repeat === "weekly" && !value.days.includes("1"))) fail();
  return `${value.time}-${Number(value.enabled)}-${{ once: 1, daily: 2, weekly: 3 }[value.repeat]}${value.repeat === "weekly" ? `-${value.days}` : ""}`;
}
export function hardwareCommandPayload(type, options = {}, context = {}) {
  if (!HARDWARE_COMMAND_TYPES.includes(type) || !object(options)) fail();
  if(context.wonlex)return wonlexSettingPayload(type,options,context,()=>hardwareCommandPayload(type,options));
  if(WONLEX_ONLY_TYPES.includes(type))fail();
  // V48 pp6–7: lifecycle commands carry no parameters. Never queue offline/retry.
  if (["RESET", "POWEROFF", "FACTORY"].includes(type)) {
    if (!exact(options, [])) fail();
    return type;
  }
  if (type === "PW") {
    // V48 p4 supplies a six-digit example; broader formats are not advertised.
    if (!exact(options, ["password"]) || typeof options.password !== "string" || !/^[0-9]{6}$/.test(options.password)) fail();
    return `PW,${options.password}`;
  }
  if (type === "MONITOR") {
    if (!exact(options, ["number"]) || typeof options.number !== "string" || !/^\+?[0-9]{6,15}$/.test(options.number)) fail();
    return `MONITOR,${options.number}`;
  }
  if (type === "LSSET") {
    // V48 p12/V48C p3 specify a fixed +6; CONFIG +8 is a separate readout.
    if (!exact(options, ["level"]) || !integer(options.level, 1, 6)) fail();
    return `LSSET,${options.level}+6`;
  }
  if (type === "WIFIFENCE") {
    if (!exact(options, ["addresses"]) || !Array.isArray(options.addresses) || options.addresses.length !== 3
      || !options.addresses.every(v => typeof v === "string" && /^(?:[0-9A-F]{2}:){5}[0-9A-F]{2}$/.test(v)
        && (parseInt(v.slice(0, 2), 16) & 1) === 0 && v !== "00:00:00:00:00:00") || new Set(options.addresses).size !== 3) fail();
    return `WIFIFENCE,${options.addresses.map((v, i) => `${i + 1},${v}`).join(",")}`;
  }
  if (type === "SPOF") {
    // The two slot-0 examples are in V48C p3; V52 p4 defines the shared fields.
    if (!exact(options, ["time", "enabled", "mode", "days"]) || !validClockTime(options.time) || !boolean(options.enabled)
      || !["on", "off"].includes(options.mode) || typeof options.days !== "string" || !/^[01]{7}$/.test(options.days)
      || !options.days.includes("1")) fail();
    const [hour, minute] = options.time.split(":").map(Number);
    return `SPOF,${hour},${minute},${Number(options.enabled)},0,${options.mode === "on" ? 0 : 1},${options.days}`;
  }
  if (MEASUREMENT_COMMAND_TYPES.includes(type)) {
    if (!exact(options, [])) fail();
    return type === "HRTSTART" ? "hrtstart,1" : "BODYTEMP2";
  }
  if (["REMOVESMS", "PEDO", "SOSSMS", "LOWBAT", "SMSONOFF", ...CALLING_CONTROL_TYPES].includes(type)) {
    if (!exact(options, ["enabled"]) || !boolean(options.enabled)) fail();
    return `${type},${Number(options.enabled)}`;
  }
  if (type === "PROFILE") {
    if (!exact(options, ["scene"]) || !integer(options.scene, 1, 4)) fail();
    return `profile,${options.scene}`;
  }
  if (type === "FALLDOWN") {
    if (!exact(options, ["enabled", "callEnabled"]) || !boolean(options.enabled) || !boolean(options.callEnabled)) fail();
    return `FALLDOWN,${Number(options.enabled)},${Number(options.callEnabled)}`;
  }
  if (type === "SOS") {
    if (!exact(options, ["numbers"]) || !Array.isArray(options.numbers) || options.numbers.length !== 3
      || !options.numbers.some(Boolean) || !options.numbers.every(n => typeof n === "string" && (n === "" || /^\+?[0-9]{6,15}$/.test(n)))
      || new Set(options.numbers.filter(Boolean)).size !== options.numbers.filter(Boolean).length) fail();
    return `SOS,${options.numbers.join(",")}`;
  }
  if(type === "PHBX"){
    if(!exact(options,["slot","name","number"])||!integer(options.slot,1,15)||typeof options.name!=="string"||options.name.length<1||options.name.length>20
      ||options.name.trim()!==options.name||/[\u0000-\u001f\u007f-\u009f\ud800-\udfff]/u.test(options.name)||Array.from(options.name).some(c=>c.length!==1)
      ||typeof options.number!=="string"||!/^\+?[0-9]{6,15}$/.test(options.number))fail();
    const name=Array.from(options.name,c=>c.charCodeAt(0).toString(16).padStart(4,"0")).join("");
    // The current bracelet has no contact-photo function. Keep the documented
    // final picture field empty; never invent picture or delete semantics.
    return `PHBX,${options.slot},${name},${options.number},`;
  }
  if (type === "APPLOCK") {
    if (!exact(options, ["mode"]) || !["press", "handsFree"].includes(options.mode)) fail();
    // Physical V48 test, 17 September 2026: JT-0 keeps the call ringing
    // until the microphone button is pressed; JT-1 answers automatically.
    return `APPLOCK,JT-${options.mode === "press" ? 0 : 1}`;
  }
  if (type === "LZ") {
    // V48 p6: only English=0 and Chinese=1 are documented. Whole-hour
    // offsets are a bounded subset; 4G network time may override this request.
    if (!exact(options, ["language", "utcOffsetHours"]) || !["en", "zh"].includes(options.language)
      || !integer(options.utcOffsetHours, -12, 14)) fail();
    return `LZ,${options.language === "en" ? 0 : 1},${options.utcOffsetHours}`;
  }
  if (type === "HRTINTERVAL") {
    // Internal operation; the wire command is hrtstart. Source prose and example
    // disagree on 300, so continuous trials use the unambiguous >300 range.
    if (!exact(options, ["intervalSeconds"]) || !(options.intervalSeconds === 0 || integer(options.intervalSeconds, 301, 65535))) fail();
    return `hrtstart,${options.intervalSeconds}`;
  }
  if (type === "BODYTEMP") {
    if (!exact(options, ["enabled", "intervalHours"]) || !boolean(options.enabled) || !integer(options.intervalHours, 1, 12)) fail();
    return `BODYTEMP,${Number(options.enabled)},${options.intervalHours}`;
  }
  if (type === "BTTIMESET") {
    if (!exact(options, ["times"]) || !Array.isArray(options.times) || options.times.length < 1 || options.times.length > 3
      || !options.times.every(t => exact(t, ["time", "repeat"]) && validClockTime(t.time) && ["daily", "once"].includes(t.repeat))
      || new Set(options.times.map(t => t.time)).size !== options.times.length) fail();
    return `BTTIMESET,${options.times.length},${options.times.map(t => `${t.time}-${t.repeat === "daily" ? 0 : 1}`).join(",")}`;
  }
  if (type === "WALKTIME") {
    if (!exact(options, ["ranges"]) || !Array.isArray(options.ranges) || options.ranges.length !== 3
      || !options.ranges.every(r => exact(r, ["start", "end"]) && validClockTime(r.start) && validClockTime(r.end)
        && (r.start < r.end || (r.start === "00:00" && r.end === "00:00")))) fail();
    const active = options.ranges.filter(r => r.start !== r.end).sort((a, b) => a.start.localeCompare(b.start));
    if (active.some((r, i) => i > 0 && r.start <= active[i - 1].end)) fail();
    return `WALKTIME,${options.ranges.map(r => `${r.start}-${r.end}`).join(",")}`;
  }
  if (type === "REMIND") {
    if (!exact(options, ["reminders"]) || !Array.isArray(options.reminders) || options.reminders.length !== 3) fail();
    return `REMIND,${options.reminders.map(reminderText).join(",")}`;
  }
  if (type === "TAKEPILLS") {
    const keys=options.audioBase64===undefined?["slot", "reminder", "text"]:["slot", "reminder", "text", "audioBase64"];
    if (!exact(options, keys) || !integer(options.slot, 1, 3) || typeof options.text !== "string"
      || options.text.length < 1 || options.text.length > 40 || options.text.trim() !== options.text
      || Array.from(options.text).some(char => char.length !== 1) || /[\u0000-\u001f\u007f-\u009f\ud800-\udfff]/u.test(options.text)
      || (options.audioBase64!==undefined&&(typeof options.audioBase64!=="string"||options.audioBase64.length>21344||!/^IyFBTVIK[A-Za-z0-9+/]*={0,2}$/.test(options.audioBase64)))) fail();
    const encoded = Array.from(options.text, char => char.charCodeAt(0).toString(16).padStart(4, "0")).join("");
    return `TAKEPILLS,${reminderText(options.reminder)},${options.slot},${encoded},`;
  }
  fail();
}
const parseReminder = value => {
  const [time, enabled, frequency, days = "0000000"] = value.split("-");
  return { time, enabled: enabled === "1", repeat: { 1: "once", 2: "daily", 3: "weekly" }[frequency], days };
};
// Decode our validated outgoing format: a last request, not firmware read-back.
/** @returns {Record<string, any> | null} */
export function hardwareParameters(type, payload, context = {}) {
  if (typeof payload !== "string" || payload.length > 512) return null;
  if (type === "PW") return payload === "PW,******" ? { redacted: true } : null;
  const [wire, ...parts] = payload.split(",");
  if (wire.toUpperCase() !== (type==='DPHBX'?'DPHBX':hardwareReplyName(type))) return null;
  let value;
  if (["RESET", "POWEROFF", "FACTORY"].includes(type) || MEASUREMENT_COMMAND_TYPES.includes(type)) value = {};
  else if (type === "MONITOR") value = { number: parts[0] };
  else if (type === "LSSET") value = { level: Number(parts[0]?.split("+")[0]) };
  else if (type === "WIFIFENCE") value = { addresses: [parts[1], parts[3], parts[5]] };
  else if (type === "SPOF") value = { time: `${parts[0]?.padStart(2, "0")}:${parts[1]?.padStart(2, "0")}`, enabled: parts[2] === "1", mode: parts[4] === "0" ? "on" : "off", days: parts[5] };
  else if (["REMOVESMS", "PEDO", "SOSSMS", "LOWBAT", "SMSONOFF", ...CALLING_CONTROL_TYPES].includes(type)) value = { enabled: parts[0] === "1" };
  else if (type === "PROFILE") value = { scene: Number(parts[0]) };
  else if (type === "FALLDOWN") value = { enabled: parts[0] === "1", callEnabled: parts[1] === "1" };
  else if (type === "SOS") value = { numbers: parts };
  else if(type === "PHBX"&&parts.length===4&&parts[3]===""&&/^[0-9a-f]+$/i.test(parts[1]??"")&&parts[1].length%4===0)value={slot:Number(parts[0]),name:parts[1].match(/.{4}/g).map(c=>String.fromCharCode(parseInt(c,16))).join(""),number:parts[2]};
  else if (type === "APPLOCK") value = { mode: parts[0] === "JT-0" ? "press" : "handsFree" };
  else if (type === "LZ") value = { language: parts[0] === "0" ? "en" : "zh", utcOffsetHours: Number(parts[1]) };
  else if (type === "HRTINTERVAL") value = { intervalSeconds: Number(parts[0]) };
  else if (type === "BODYTEMP") value = { enabled: parts[0] === "1", intervalHours: Number(parts[1]) };
  else if (type === "BTTIMESET") value = { times: parts.slice(1).map(t => { const [time, r] = t.split("-"); return { time, repeat: r === "0" ? "daily" : "once" }; }) };
  else if (type === "WALKTIME") value = { ranges: parts.map(r => { const [start, end] = r.split("-"); return { start, end }; }) };
  else if (type === "REMIND") value = { reminders: parts.map(parseReminder) };
  else if (type === "TAKEPILLS" && /^[0-9a-f]+$/i.test(parts[2] ?? "") && parts[2].length % 4 === 0) value = {
    reminder: parseReminder(parts[0]), slot: Number(parts[1]), text: parts[2].match(/.{4}/g).map(c => String.fromCharCode(parseInt(c, 16))).join("") };
  else if(WONLEX_ONLY_TYPES.includes(type))value=wonlexDecodedParameters(type,parts);
  if(context.wonlex&&['REMIND','TAKEPILLS'].includes(type)&&value){
    for(const reminder of type==='REMIND'?value.reminders:[value.reminder])if(reminder.repeat==='weekly')reminder.days=reminder.days.slice(1)+reminder.days[0];
  }
  // Decode historical outgoing settings even if a newer CONFIG changes limits.
  const decoding=context.wonlex?{...context,fallMaximum:type==='LSSET'?Number(parts[0]?.split('+')[1]):context.fallMaximum,phonebookSlots:100}:context;
  try { return hardwareCommandPayload(type, value, decoding).toUpperCase() === payload.toUpperCase() ? value : null; } catch { return null; }
}
export function hardwareCommandKey(command, context = {}) {
  if (command.type === "SPOF") return `SPOF:${hardwareParameters(command.type, command.payload)?.mode ?? "invalid"}`;
  if(['PHBX','DPHBX'].includes(command.type))return `${command.type}:${hardwareParameters(command.type,command.payload,context)?.slot??"invalid"}`;
  return command.type === "TAKEPILLS" ? `${command.type}:${hardwareParameters(command.type, command.payload)?.slot ?? "invalid"}` : command.type;
}
export function findHardwareSettingCommand(commands, type, slot = 0, context = {}) {
  return commands?.find(c => c.type === type
    && (!['TAKEPILLS', 'PHBX','DPHBX'].includes(type) || hardwareParameters(c.type, c.payload,context)?.slot === slot)
    && (type !== 'SPOF' || hardwareParameters(c.type, c.payload)?.mode === (slot === 1 ? 'on' : 'off')));
}
export function hardwareSettingValues(type, draft, slot, context = {}) {
  if (['PHBX', 'DPHBX', 'TAKEPILLS'].includes(type)) {
    if (!integer(slot, 1, type === 'TAKEPILLS' ? 3 : context.wonlex ? context.phonebookSlots : 15)) fail();
    return {...draft, slot};
  }
  return draft;
}
export function hardwareReplyType(reply) {
  const upper = reply.toUpperCase();
  // Preserve TAKEPILLS status; its undocumented alternatives are not success.
  if (/^TAKEPILLS,[0-9]{1,3}$/.test(upper)) return "TAKEPILLS";
  return [...HARDWARE_COMMAND_TYPES, "FIND", "UPLOAD"].includes(upper) && upper !== "HRTINTERVAL" ? upper : null;
}
